import type { FunctionComponent } from "react";
import { useTheme } from "@mui/material";
import type { ManagementDisplayUnit } from "../typings/management-display-unit";
import { getManagementChartTooltipProps } from "../utils/management-chart-theme.utils";
import { formatManagementChartTooltipValue } from "../utils/management-display-unit.utils";

type StatusPeriodTooltipEntry = {
  dataKey?: string | number;
  name?: string;
  value?: number | string;
  color?: string;
};

type ManagementStatusPeriodTooltipProps = {
  active?: boolean;
  payload?: StatusPeriodTooltipEntry[];
  label?: string | number;
  displayUnit: ManagementDisplayUnit;
  notSpecifiedDataKey: string;
};

/**
 * Status period bar tooltip — Office/Mobile keep series colors; Not specified uses text.secondary.
 * Styles come from the theme (not Tooltip contentStyle props — Recharts overwrites those on clone).
 */
export const ManagementStatusPeriodTooltip: FunctionComponent<
  ManagementStatusPeriodTooltipProps
> = ({ active, payload, label, displayUnit, notSpecifiedDataKey }) => {
  const theme = useTheme();
  const { contentStyle, labelStyle } = getManagementChartTooltipProps(theme);
  const notSpecifiedColor = theme.palette.text.secondary;

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      style={{
        ...contentStyle,
        margin: 0,
        padding: 10,
        whiteSpace: "nowrap"
      }}
      data-testid="management-status-period-tooltip"
    >
      {label != null && label !== "" && (
        <p style={{ ...labelStyle, margin: 0, marginBottom: 4 }}>{label}</p>
      )}
      <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
        {payload.map(entry => {
          const dataKey = String(entry.dataKey ?? "");
          const color =
            dataKey === notSpecifiedDataKey ? notSpecifiedColor : entry.color ?? notSpecifiedColor;

          return (
            <li key={dataKey} style={{ color, paddingTop: 4, paddingBottom: 4 }}>
              {entry.name}
              {" : "}
              {formatManagementChartTooltipValue(entry.value, displayUnit)}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
